import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { EmbedParentMessageType, postMessageToEmbedParent } from 'utils/embed/embedParentMessage'
import { readEmbedFrameContext, resolveEmbedParentOrigin } from 'utils/embed/embedParentOrigin'
import { embedSession } from 'utils/embedSession'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** What made the screen close: the broker pressing Close, or the auto-close countdown expiring. */
export type EmbedCloseTrigger = 'manual' | 'auto_countdown'

export interface EmbedCloseOptions {
  /** Seconds before the screen closes itself; omit to keep the close manual-only. */
  autoCloseAfterSeconds?: number
}

/**
 * Whole positive seconds only. A 0 would close instantly with no countdown ever shown, and a
 * fractional or negative one would tick past the expiry check forever, leaking a 1 Hz interval —
 * so anything else degrades to manual-only instead of arming a broken countdown.
 */
const armedSeconds = (seconds: number | undefined): number | null =>
  seconds !== undefined && Number.isInteger(seconds) && seconds > 0 ? seconds : null

/**
 * Close affordance of an embed terminal screen (order confirmation, cancelled screen): resolves
 * the allowlisted parent origin once and posts BACKBONE_CLOSE on close. The contract forbids a
 * wildcard targetOrigin, so `canClose` is false when no allowlisted parent origin can be resolved;
 * the fail-closed resolver hides the button without any error, and the surface-specific
 * `unavailableEvent` is then the only trace it fired.
 *
 * `eventProps` is folded into both events (e.g. the screen variant); the click event additionally
 * carries `trigger` — manual or auto — and `posted` — whether the message actually went out — so a
 * dropped post is distinguishable from a successful close in telemetry.
 *
 * With `autoCloseAfterSeconds` the screen also closes itself (ISSUE-3171): `secondsUntilAutoClose`
 * counts down for the caller to render, expiry runs the exact same close path as the button, and
 * the click event gains `secondsRemaining` so an instant dismissal is distinguishable from a read.
 * The countdown is gated on `canClose`, so an unresolvable parent origin arms no timer at all.
 *
 * Only the AUTO close is once-per-mount. `handleClose` always posts and always emits, because the
 * button is the broker's only exit if the parent never acted on the automatic message: re-posting
 * is harmless (the targetOrigin is pinned, and a dropped first message is exactly the case where a
 * retry is wanted), whereas a silently inert button is not. Any close disarms the countdown.
 */
export const useEmbedClose = (
  clickEvent: AnalyticsEvent,
  unavailableEvent: AnalyticsEvent,
  eventProps: Record<string, unknown> = {},
  { autoCloseAfterSeconds }: EmbedCloseOptions = {}
) => {
  const frameContext = useMemo(() => readEmbedFrameContext(embedSession.getProvider() ?? 'propstack'), [])
  const { origin: parentOrigin, denialReason } = useMemo(() => resolveEmbedParentOrigin(frameContext), [frameContext])
  const canClose = parentOrigin !== null

  // Deliberately not an effect dependency: eventProps is an inline object at the call sites and
  // the event must fire once per denial, not once per render.
  const eventPropsRef = useRef(eventProps)
  eventPropsRef.current = eventProps

  // `canClose` and `autoCloseAfterSeconds` are fixed for the lifetime of the screen, so this is the
  // whole arming decision — no re-render can restart the countdown.
  const initialSeconds = canClose ? armedSeconds(autoCloseAfterSeconds) : null
  const isAutoCloseArmed = initialSeconds !== null
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(initialSeconds)

  // Read by `close` without making it a dependency: a per-tick identity change would re-run the
  // expiry effect on every second for nothing.
  const secondsRemainingRef = useRef(secondsRemaining)
  secondsRemainingRef.current = secondsRemaining

  useEffect(() => {
    if (denialReason !== null) {
      logAnalyticsEvent(unavailableEvent, { reason: denialReason, ...eventPropsRef.current })
    }
  }, [denialReason, unavailableEvent])

  const close = useCallback((trigger: EmbedCloseTrigger) => {
    setSecondsRemaining(null)
    const posted = postMessageToEmbedParent(EmbedParentMessageType.CLOSE, frameContext)
    // Null once the countdown is over, which is 0 seconds remaining from a reporting standpoint.
    const timing = isAutoCloseArmed ? { secondsRemaining: secondsRemainingRef.current ?? 0 } : {}
    logAnalyticsEvent(clickEvent, { ...eventPropsRef.current, trigger, posted, ...timing })
  }, [clickEvent, frameContext, isAutoCloseArmed])

  // Zero-argument so it can be handed straight to onClick without the click event landing in the
  // trigger argument.
  const handleClose = useCallback(() => close('manual'), [close])

  // Depends on the boolean, not on the seconds: ticking must not re-arm the interval. It flips only
  // when the countdown ends — on expiry or on any close — which is when it has to be cleared.
  const isCountingDown = secondsRemaining !== null
  useEffect(() => {
    if (!isCountingDown) return
    const interval = setInterval(
      () => setSecondsRemaining((remaining) => remaining === null || remaining <= 0 ? remaining : remaining - 1),
      1000
    )
    return () => clearInterval(interval)
  }, [isCountingDown])

  const hasAutoClosedRef = useRef(false)
  useEffect(() => {
    if (secondsRemaining === null || secondsRemaining > 0 || hasAutoClosedRef.current) return
    hasAutoClosedRef.current = true
    close('auto_countdown')
  }, [secondsRemaining, close])

  return {
    canClose,
    handleClose,
    // 0 is the expiry tick rather than a label — the effect above closes on the same commit.
    secondsUntilAutoClose: secondsRemaining !== null && secondsRemaining > 0 ? secondsRemaining : null,
  }
}
